import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import authService from '@/services/authService';
import customerService, {
  CustomerDetailResponse,
} from '@/services/customerService';
import {
  getBookingCalendar,
  createManualBooking,
  type CalendarField,
} from '@/services/bookingCalendarService';
import ManualBookingDrawer from '@/components/admin/booking/ManualBookingDrawer';
import InvoiceDetailModal from '@/components/admin/InvoiceDetailModal';
import EditCustomerModal from './EditCustomerModal';
import { cn } from '@/lib/utils';

export const CustomerDetailPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const arenaSlug = authService.getSlug() || localStorage.getItem('arenaSlug') || '';
  const userRole = authService.getRole();
  const isCashier = userRole === 'staff';

  const [detail, setDetail] = useState<CustomerDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [bookingFilter, setBookingFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const [bookingPage, setBookingPage] = useState(1);
  const BOOKING_PAGE_SIZE = 10;

  // Manual booking drawer state
  const [fields, setFields] = useState<CalendarField[]>([]);
  const [manualTarget, setManualTarget] = useState<{
    fieldSlug: string;
    date: string;
    start: string;
    end: string;
  } | null>(null);

  // Edit customer modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Invoice detail modal state
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState<string | null>(null);

  const fetchDetail = useCallback(
    async (page: number, filter: 'all' | 'upcoming' | 'past', isInitial = false) => {
      if (!arenaSlug || !customerId) return;
      try {
        if (isInitial) {
          setLoading(true);
        } else {
          setTableLoading(true);
        }
        const res = await customerService.getCustomerDetail(arenaSlug, customerId, {
          booking_page: page,
          booking_limit: BOOKING_PAGE_SIZE,
          booking_filter: filter,
        });
        setDetail(res);
      } catch (err) {
        showToast.error(getApiErrorMessage(err, 'Gagal memuat detail pelanggan'));
      } finally {
        setLoading(false);
        setTableLoading(false);
      }
    },
    [arenaSlug, customerId]
  );

  useEffect(() => {
    fetchDetail(1, 'all', true);
  }, [fetchDetail]);

  const handlePageChange = (newPage: number) => {
    setBookingPage(newPage);
    fetchDetail(newPage, bookingFilter, false);
  };

  const handleFilterChange = (newFilter: 'all' | 'upcoming' | 'past') => {
    setBookingFilter(newFilter);
    setBookingPage(1);
    fetchDetail(1, newFilter, false);
  };

  // Load fields for manual booking
  useEffect(() => {
    if (!arenaSlug) return;
    const today = new Date().toISOString().split('T')[0];
    void getBookingCalendar(arenaSlug, today, today, null)
      .then((res) => {
        setFields(res.fields || []);
      })
      .catch(() => {});
  }, [arenaSlug]);

  const handleOpenManualBooking = () => {
    const today = new Date().toISOString().split('T')[0];
    const defaultField = fields[0]?.slug || '';
    setManualTarget({
      fieldSlug: defaultField,
      date: today,
      start: '',
      end: '',
    });
  };

  const handleManualBookingSubmit = async (payload: {
    team_name: string;
    phone_number: string;
    description?: string;
    field_slug: string;
    booking_date: string;
    selected_slots: string[];
    payment_status: 'paid' | 'dp_paid' | 'unpaid';
  }) => {
    if (!arenaSlug) return;
    await createManualBooking(arenaSlug, payload);
    showToast.success('Booking manual berhasil dibuat');
    setManualTarget(null);
    await fetchDetail(bookingPage, bookingFilter);
  };

  const getInitials = (name: string) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const openWhatsApp = () => {
    if (!detail?.customer.phone) {
      showToast.error('Nomor HP tidak tersedia');
      return;
    }
    const cleanPhone = detail.customer.phone.replace(/\D/g, '').replace(/^0/, '62');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const formatRupiah = (val: number) => {
    return 'Rp. ' + Math.round(val).toLocaleString('id-ID');
  };

  const bookings = detail?.bookings || [];
  const totalItems = detail?.booking_pagination?.total_items ?? 0;
  const totalPages = Math.max(
    1,
    detail?.booking_pagination?.total_pages ?? Math.ceil(totalItems / BOOKING_PAGE_SIZE)
  );

  const renderPaginationButtons = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (bookingPage > 3) pages.push('...');
      const start = Math.max(2, bookingPage - 1);
      const end = Math.min(totalPages - 1, bookingPage + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (bookingPage < totalPages - 2) pages.push('...');
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }

    return (
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={bookingPage <= 1 || tableLoading}
          onClick={() => handlePageChange(Math.max(1, bookingPage - 1))}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E0E6ED] bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        >
          <Icon icon="ph:caret-left-bold" className="h-3.5 w-3.5" />
        </button>

        {pages.map((p, idx) =>
          p === '...' ? (
            <span key={`dots-${idx}`} className="px-1 text-xs text-gray-400">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              disabled={tableLoading}
              onClick={() => handlePageChange(Number(p))}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors cursor-pointer',
                p === bookingPage
                  ? 'bg-[#FC480D] text-white shadow-2xs'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              )}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          disabled={bookingPage >= totalPages || tableLoading}
          onClick={() => handlePageChange(Math.min(totalPages, bookingPage + 1))}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E0E6ED] bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        >
          <Icon icon="ph:caret-right-bold" className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  // Max weekly booking count for chart scaling
  const maxWeeklyCount = useMemo(() => {
    if (!detail?.weekly_bookings || detail.weekly_bookings.length === 0) return 1;
    const max = Math.max(...detail.weekly_bookings.map((b) => b.count));
    return max > 0 ? max : 1;
  }, [detail?.weekly_bookings]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-14 animate-pulse">
        <div className="h-10 w-48 bg-gray-200 rounded-xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 bg-white rounded-2xl border border-gray-100 p-5 space-y-3" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-72 bg-white rounded-2xl border border-gray-100 p-6" />
          <div className="h-72 bg-white rounded-2xl border border-gray-100 p-6" />
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-6 max-w-lg mx-auto my-12 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center mx-auto text-rose-500">
          <Icon icon="ph:warning-circle-bold" className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">Pelanggan Tidak Ditemukan</h2>
        <p className="text-sm text-gray-500">
          Data pelanggan yang dicari tidak ada atau telah dihapus.
        </p>
        <button
          type="button"
          onClick={() => navigate('/admin/customers')}
          className="px-5 py-2.5 rounded-xl bg-gray-900 text-white font-medium text-sm hover:bg-gray-800 transition-colors cursor-pointer"
        >
          Kembali ke Daftar Pelanggan
        </button>
      </div>
    );
  }

  const { customer, stats, habits, weekly_bookings, date_range_label, booking_counts } = detail;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-14">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/customers')}
            className="w-9 h-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors cursor-pointer shrink-0"
          >
            <Icon icon="ph:arrow-left-bold" className="w-4 h-4" />
          </button>

          <div className="w-11 h-11 rounded-full bg-orange-100 text-orange-600 font-bold text-sm flex items-center justify-center shrink-0 uppercase tracking-wider">
            {getInitials(customer.name)}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-[#0E1726]">{customer.name}</h1>
              {!customer.is_app_user && (
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(true)}
                  title="Edit info kontak pelanggan"
                  className="w-6 h-6 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500 font-mono mt-0.5">
              <span>{customer.formatted_phone}</span>
              {customer.email && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="font-sans text-gray-400 text-xs">{customer.email}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          {customer.phone && (
            <button
              type="button"
              onClick={openWhatsApp}
              className="h-10 px-4 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs sm:text-sm font-semibold transition-colors cursor-pointer shadow-2xs"
            >
              <Icon icon="ri:whatsapp-fill" className="w-4 h-4 text-emerald-600" />
              <span>Chat WhatsApp</span>
            </button>
          )}

          <button
            type="button"
            onClick={handleOpenManualBooking}
            className="h-10 px-5 inline-flex items-center gap-2 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs sm:text-sm font-semibold shadow-xs transition-colors cursor-pointer"
          >
            <Icon icon="ph:plus-bold" className="w-4 h-4" />
            <span>Tambah Booking</span>
          </button>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Booking */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-2xs space-y-1">
          <p className="text-xs font-medium text-gray-400">Total booking</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total_booking}</p>
          <p className="text-xs text-gray-400">
            {stats.upcoming_bookings_count > 0
              ? `${stats.upcoming_bookings_count} booking mendatang`
              : 'Tidak ada booking mendatang'}
          </p>
        </div>

        {/* Jam Main */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-2xs space-y-1">
          <p className="text-xs font-medium text-gray-400">Jam main</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total_jam_main} jam</p>
          <p className="text-xs text-gray-400">
            Rata-rata {stats.avg_jam_main_per_booking} jam / booking
          </p>
        </div>

        {/* Total Belanja (Hidden for Cashier) */}
        {!isCashier ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-2xs space-y-1">
            <p className="text-xs font-medium text-gray-400">Total belanja</p>
            <p className="text-2xl font-bold text-gray-900">{formatRupiah(stats.total_belanja)}</p>
            <p className="text-xs text-gray-400">
              Rata-rata {formatRupiah(stats.avg_belanja_per_booking)} / booking
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-2xs space-y-1 opacity-75">
            <p className="text-xs font-medium text-gray-400">Status Akses Kasir</p>
            <p className="text-base font-semibold text-gray-700">Terbatas</p>
            <p className="text-xs text-gray-400">Data keuangan disembunyikan</p>
          </div>
        )}

        {/* Belum Dibayar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-2xs space-y-1">
          <p className="text-xs font-medium text-gray-400">Belum dibayar</p>
          <p
            className={`text-2xl font-bold ${
              stats.unpaid_amount > 0 ? 'text-rose-600' : 'text-gray-900'
            }`}
          >
            {formatRupiah(stats.unpaid_amount)}
          </p>
          <p className="text-xs text-gray-400">
            {stats.unpaid_amount === 0
              ? 'Semua sudah lunas'
              : `${stats.unpaid_count} transaksi belum lunas`}
          </p>
        </div>
      </div>

      {/* Middle Grid: Weekly Booking Chart & Kebiasaan Main */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Weekly Booking Chart (7 cols) */}
        <div className="lg:col-span-7 bg-white rounded-2xl border border-gray-100 p-6 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between gap-4 mb-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400">
              Booking per Minggu
            </h2>
            <span className="text-xs font-medium text-gray-400">{date_range_label}</span>
          </div>

          {/* Bar Chart Container */}
          <div className="pt-2 pb-1">
            <div className="flex items-end justify-between gap-2 h-44">
              {weekly_bookings.map((bar, idx) => {
                const CHART_MAX_PX = 110;
                const barHeightPx =
                  bar.count > 0
                    ? Math.max(20, Math.round((bar.count / maxWeeklyCount) * CHART_MAX_PX))
                    : 4;
                const isHighlight = bar.count > 0;

                return (
                  <div
                    key={idx}
                    className="flex-1 flex flex-col items-center justify-end h-full group min-w-0"
                  >
                    {/* Count label */}
                    <div
                      className={`text-[11px] font-bold mb-1.5 transition-transform group-hover:-translate-y-0.5 ${
                        isHighlight ? 'text-[#FC480D]' : 'text-gray-400'
                      }`}
                    >
                      {bar.count}
                    </div>

                    {/* Bar column area with fixed height */}
                    <div
                      className="w-full flex items-end justify-center"
                      style={{ height: `${CHART_MAX_PX}px` }}
                    >
                      <div
                        style={{ height: `${barHeightPx}px` }}
                        className={`w-7 sm:w-8.5 rounded-t-md transition-all duration-300 ${
                          isHighlight
                            ? 'bg-[#FC480D] shadow-xs'
                            : 'bg-gray-200/90 group-hover:bg-gray-300'
                        }`}
                      />
                    </div>

                    {/* Week date label */}
                    <span className="text-[10px] sm:text-[11px] text-gray-400 mt-2 truncate max-w-full text-center">
                      {bar.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Kebiasaan Main Card (5 cols) */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-gray-100 p-6 shadow-2xs flex flex-col justify-between">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">
              Kebiasaan Main
            </h2>

            <div className="space-y-3.5 text-xs sm:text-sm">
              <div className="flex items-center justify-between py-1 border-b border-gray-50">
                <span className="text-gray-400">Pelanggan sejak</span>
                <span className="font-semibold text-gray-900">{habits.member_since}</span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-gray-50">
                <span className="text-gray-400">Lapangan favorit</span>
                <span className="font-semibold text-gray-900">{habits.favorite_field || '-'}</span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-gray-50">
                <span className="text-gray-400">Jam favorit</span>
                <span className="font-semibold text-gray-900">{habits.favorite_time || '-'}</span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-gray-50">
                <span className="text-gray-400">Hari favorit</span>
                <span className="font-semibold text-gray-900">{habits.favorite_day || '-'}</span>
              </div>

              {/* Loyalty Row (PL-24: Only shown when active) */}
              {habits.loyalty_status && (
                <div className="flex items-center justify-between py-1">
                  <span className="text-gray-400">Status loyalty</span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200/70">
                    <Icon icon="ph:medal-fill" className="w-3.5 h-3.5" />
                    <span>{habits.loyalty_status}</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-400">
            Dianalisis dari riwayat jadwal booking selesai
          </div>
        </div>
      </div>

      {/* Riwayat Booking Table Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-2xs overflow-hidden">
        {/* Table Header & Filter Tabs */}
        <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400">
            Riwayat Booking
          </h2>

          <div className="flex items-center gap-1.5 self-start sm:self-auto bg-gray-50/80 p-1 rounded-full border border-gray-200/60">
            <button
              type="button"
              disabled={tableLoading}
              onClick={() => handleFilterChange('all')}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                bookingFilter === 'all'
                  ? 'bg-white text-[#FC480D] border border-orange-200 shadow-2xs font-bold'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Semua {booking_counts.all}
            </button>
            <button
              type="button"
              disabled={tableLoading}
              onClick={() => handleFilterChange('upcoming')}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                bookingFilter === 'upcoming'
                  ? 'bg-white text-[#FC480D] border border-orange-200 shadow-2xs font-bold'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Akan datang {booking_counts.upcoming}
            </button>
            <button
              type="button"
              disabled={tableLoading}
              onClick={() => handleFilterChange('past')}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                bookingFilter === 'past'
                  ? 'bg-white text-[#FC480D] border border-orange-200 shadow-2xs font-bold'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Selesai {booking_counts.past}
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto relative">
          {tableLoading && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              <Icon icon="ph:spinner-bold" className="w-6 h-6 animate-spin text-[#FC480D]" />
            </div>
          )}
          <table className="w-full text-left text-xs sm:text-sm border-collapse min-w-[750px]">
            <thead>
              <tr className="border-b border-gray-100 text-gray-400 font-semibold text-[11px] sm:text-xs">
                <th className="py-3.5 px-4 sm:px-6">Tanggal</th>
                <th className="py-3.5 px-4">ID</th>
                <th className="py-3.5 px-4">Lapangan</th>
                <th className="py-3.5 px-4">Jam</th>
                <th className="py-3.5 px-4">Sumber</th>
                <th className="py-3.5 px-4 text-center">Status</th>
                <th className="py-3.5 px-4 sm:px-6 text-right">Nominal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400 text-xs sm:text-sm">
                    Tidak ada riwayat booking dalam kategori ini.
                  </td>
                </tr>
              ) : (
                bookings.map((b) => {
                  const isPaid =
                    b.status === 'paid' ||
                    b.status_label.toLowerCase() === 'paid' ||
                    b.status_label.toLowerCase() === 'lunas';
                  const isDp =
                    b.status === 'dp_paid' || b.status_label.toLowerCase().includes('dp');

                  return (
                    <tr
                      key={b.id}
                      onClick={() => {
                        if (b.invoice_number) {
                          setSelectedInvoiceNumber(b.invoice_number);
                        }
                      }}
                      className="hover:bg-orange-50/30 transition-colors cursor-pointer group"
                    >
                      <td className="py-4 px-4 sm:px-6 font-medium text-gray-900">
                        {b.booking_date}
                      </td>
                      <td className="py-4 px-4 font-mono font-semibold text-gray-700">
                        {b.order_number}
                      </td>
                      <td className="py-4 px-4 text-gray-600">{b.field_name}</td>
                      <td className="py-4 px-4 text-gray-600">{b.time_range}</td>
                      <td className="py-4 px-4 text-gray-600">{b.source}</td>
                      <td className="py-4 px-4 text-center">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
                            isPaid
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : isDp
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200'
                          }`}
                        >
                          {b.status_label}
                        </span>
                      </td>
                      <td className="py-4 px-4 sm:px-6 text-right font-semibold text-gray-900">
                        {formatRupiah(b.nominal)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer: Summary & Pagination */}
        <div className="p-4 sm:p-6 border-t border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-500">
            Menampilkan{' '}
            <span className="font-semibold text-gray-700">
              {totalItems === 0 ? 0 : (bookingPage - 1) * BOOKING_PAGE_SIZE + 1}
            </span>{' '}
            -{' '}
            <span className="font-semibold text-gray-700">
              {Math.min(bookingPage * BOOKING_PAGE_SIZE, totalItems)}
            </span>{' '}
            dari <span className="font-semibold text-gray-700">{totalItems.toLocaleString('id-ID')}</span>
          </p>
          <div>{renderPaginationButtons()}</div>
        </div>
      </div>

      {/* Manual Booking Drawer */}
      <ManualBookingDrawer
        target={manualTarget}
        fields={fields}
        arenaSlug={arenaSlug}
        onClose={() => {
          setManualTarget(null);
        }}
        onSubmit={handleManualBookingSubmit}
        initialCustomerName={customer.name}
        initialPhone={customer.phone}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        customer={{
          id: customer.id,
          name: customer.name,
          phone: customer.phone,
          is_app_user: customer.is_app_user,
        }}
        onSuccess={() => fetchDetail(bookingPage, bookingFilter)}
      />

      {/* Invoice Detail Modal */}
      {selectedInvoiceNumber && (
        <InvoiceDetailModal
          isOpen={!!selectedInvoiceNumber}
          onClose={() => setSelectedInvoiceNumber(null)}
          invoiceNumber={selectedInvoiceNumber}
        />
      )}
    </div>
  );
};

export default CustomerDetailPage;
